/**
 * Users admin list (ADMIN-PR1-b) — GET /v1/admin/users with email-search (q),
 * status filter, and offset/limit pagination. Rows link to the detail screen.
 * (Dual-gated: adminApi sends X-OXBO-Admin-Mode + super_admin token.)
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { listUsers, type UserListResponse } from "../auth/adminApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";

const PAGE_SIZE = 25;
const STATUS_FILTERS = ["", "active", "suspended"] as const;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function UsersPage(): ReactNode {
  // `query` is the committed search term; `term` is the live input.
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string>("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error } = useAsync<UserListResponse>(
    () => listUsers({ q: query, status, limit: PAGE_SIZE, offset }),
    [query, status, offset],
  );

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setOffset(0);
    setQuery(term.trim());
  }

  const total = data?.total ?? 0;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <div>
      <PageHeader title="Users" />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Search by email…"
            aria-label="Search by email"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="w-64 rounded border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600" htmlFor="user-status">
            Status
          </label>
          <select
            id="user-status"
            value={status}
            onChange={(e) => {
              setOffset(0);
              setStatus(e.target.value);
            }}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s || "all"} value={s}>
                {s === "" ? "all" : s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.users.length === 0 ? (
        <EmptyState message={query ? `No users match “${query}”.` : "No users."} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Org roles</th>
                  <th className="px-3 py-2 font-medium">System roles</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link to={`/users/${u.id}`} className="text-oxbo hover:underline">
                        {u.email}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{u.full_name ?? "—"}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {u.org_roles.length ? u.org_roles.join(", ") : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {u.system_roles.length ? u.system_roles.join(", ") : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
            <span>
              {total === 0 ? "0" : `${offset + 1}–${showingTo}`} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={showingTo >= total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
